/**
 * R-3 — surfaces citation density gate block with regen / override paths.
 */
import React, { useState } from 'react';
import { AlertTriangle, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';
import type { CitationDensitySnapshot } from '../../lib/audit/citationDensityGate';
import { OVERRIDE_RATIONALE_MIN_CHARS } from '../../lib/audit/citationDensityGate';
import { useStore } from '../../store';
import type { ReportTypeId } from '../../config/reportTypes';

export interface CitationDensityOverrideBannerProps {
  siteId: string;
  reportTypeId: ReportTypeId;
  snapshot: CitationDensitySnapshot;
  /** When true, regen-with-focus was already used for this block. */
  regenFocusUsed?: boolean;
  onRetryDispatch: (opts?: {
    allowCitationDensityOverride?: boolean;
    citationDensityRegenerateFocus?: boolean;
  }) => void;
  onCancel?: () => void;
  testId?: string;
}

export function CitationDensityOverrideBanner({
  siteId,
  reportTypeId,
  snapshot,
  regenFocusUsed = false,
  onRetryDispatch,
  onCancel,
  testId = 'citation-density-override-banner',
}: CitationDensityOverrideBannerProps) {
  const recordAuditOverride = useStore(s => s.recordAuditOverride);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [rationale, setRationale] = useState('');
  const [busy, setBusy] = useState(false);

  const densityPct = Math.round(snapshot.density * 100);
  const thresholdPct = Math.round(snapshot.threshold * 100);
  const topUncited = snapshot.uncitedClaims.slice(0, 5);

  const handleOverride = async () => {
    if (rationale.trim().length < OVERRIDE_RATIONALE_MIN_CHARS) return;
    setBusy(true);
    try {
      await recordAuditOverride(siteId, {
        type: 'density',
        threshold: snapshot.density,
        rationale: rationale.trim(),
        reportTypeId,
      });
      const { logAuditEvent } = await import('../../lib/telemetry/auditTelemetry');
      logAuditEvent({
        type: 'citation-density-gate-override' as Parameters<typeof logAuditEvent>[0]['type'],
        siteId,
        reportType: reportTypeId,
        payload: {
          density: snapshot.density,
          threshold: snapshot.threshold,
          uncitedCount: snapshot.uncitedClaims.length,
          rationaleLength: rationale.trim().length,
        },
      });
      setShowOverrideModal(false);
      setRationale('');
      onRetryDispatch({ allowCitationDensityOverride: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="border border-destructive/40 bg-destructive/5 p-4 space-y-3"
      data-testid={testId}
      role="alert"
    >
      <div className="flex items-start gap-2">
        <AlertTriangle className="w-4 h-4 text-destructive shrink-0 mt-0.5" aria-hidden />
        <div className="space-y-1">
          <div className="type-body font-bold uppercase tracking-widest text-destructive">
            Citation density below floor
          </div>
          <p className="text-[11px] text-text-secondary leading-relaxed">
            {densityPct}% of claims cite a source (floor {thresholdPct}%). Regenerate with citation focus
            or override with rationale to proceed.
          </p>
          <p className="text-[11px] text-text-muted tabular-nums">
            {snapshot.citedClaims} cited / {snapshot.totalClaims} total · source: {snapshot.source}
          </p>
        </div>
      </div>

      {topUncited.length > 0 && (
        <div className="space-y-1" data-testid="citation-density-uncited-list">
          <p className="text-[10px] uppercase tracking-widest text-text-secondary font-semibold">
            Top uncited claims
          </p>
          <ul className="list-disc pl-5 text-[11px] text-text-secondary space-y-0.5">
            {topUncited.map((c) => (
              <li key={`${c.index}-${c.text.slice(0, 40)}`}>{c.text}</li>
            ))}
          </ul>
          {snapshot.uncitedClaims.length > 5 && (
            <p className="text-[10px] text-text-muted">
              …and {snapshot.uncitedClaims.length - 5} more
            </p>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          variant="default"
          disabled={busy || regenFocusUsed}
          onClick={() => onRetryDispatch({ citationDensityRegenerateFocus: true })}
          data-testid="citation-density-regen-focus-btn"
        >
          {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <RefreshCw className="w-3 h-3 mr-1" />}
          Regenerate with citation focus
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => setShowOverrideModal(true)}
          data-testid="citation-density-override-btn"
        >
          Override with rationale
        </Button>
        {onCancel && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            disabled={busy}
            onClick={onCancel}
            data-testid="citation-density-cancel-btn"
          >
            Cancel
          </Button>
        )}
      </div>

      {regenFocusUsed && (
        <p className="text-[10px] text-amber-600" data-testid="citation-density-regen-used-hint">
          Citation-focus regeneration already used for this block.
        </p>
      )}

      {showOverrideModal && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-black/40 p-4"
          data-testid="citation-density-override-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="citation-density-override-title"
        >
          <div className="w-full max-w-md bg-background border border-border p-4 space-y-3 shadow-lg">
            <div id="citation-density-override-title" className="type-body font-bold uppercase tracking-widest">
              Override citation density gate
            </div>
            <p className="text-[11px] text-text-secondary">
              Document why this report should publish below the {thresholdPct}% citation floor
              (minimum {OVERRIDE_RATIONALE_MIN_CHARS} characters). Unblocks this dispatch only.
            </p>
            <textarea
              className="w-full min-h-[100px] border border-border p-2 text-sm"
              value={rationale}
              onChange={e => setRationale(e.target.value)}
              data-testid="citation-density-override-rationale"
              placeholder="Explain why uncited claims are acceptable for this dispatch…"
            />
            <div className="text-[10px] text-text-muted">
              {rationale.trim().length}/{OVERRIDE_RATIONALE_MIN_CHARS} characters minimum
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setShowOverrideModal(false);
                  setRationale('');
                }}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                variant="default"
                disabled={busy || rationale.trim().length < OVERRIDE_RATIONALE_MIN_CHARS}
                onClick={() => void handleOverride()}
                data-testid="citation-density-override-confirm"
              >
                Confirm override
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
